import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { TourStep } from './tourTypes';

/** Mesmo mínimo do avanço automático, para os dois critérios não divergirem. */
const MIN_SEARCH_LENGTH = 3;

/** Existe, está no documento e ocupa espaço (aba fechada não conta). */
function isVisible(tourId: string): boolean {
	const el = document.querySelector<HTMLElement>(`[data-tour="${tourId}"]`);

	if (!el || !el.isConnected) return false;

	const box = el.getBoundingClientRect();

	return box.width > 0 || box.height > 0;
}

function readInput(tourId: string | undefined): string {
	if (!tourId) return '';

	const scope = document.querySelector<HTMLElement>(`[data-tour="${tourId}"]`);
	const field = scope?.querySelector<HTMLInputElement>('input, textarea');

	return (field?.value ?? '').trim();
}

function isInputFilled(tourId: string | undefined): boolean {
	return readInput(tourId).length >= MIN_SEARCH_LENGTH;
}

/**
 * Tira acento, maiúscula e pontuação.
 *
 * "529.982.247-25" e "52998224725" viram a mesma coisa, e "Joana Ribeiro" casa com
 * "JOANA RIBEIRO ALVES". Sem isso a conferência reprovaria quem digitou certo.
 */
function normalize(text: string): string {
	return text
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '');
}

/** O que a pessoa digitou aparece mesmo na lista que está na tela? */
function isSearchReflected(inputTourId: string, resultsTourId: string): boolean {
	const term = normalize(readInput(inputTourId));

	if (term.length < MIN_SEARCH_LENGTH) return false;

	const results = document.querySelector<HTMLElement>(`[data-tour="${resultsTourId}"]`);

	if (!results) return false;

	return normalize(results.innerText ?? '').includes(term);
}

/**
 * A pessoa realmente fez o que o passo pedia?
 *
 * Checa só estado observável — rota atual, elemento na tela, campo preenchido.
 * De propósito não pergunta nada às telas de negócio: o tour continua sendo algo
 * que dá para apagar inteiro sem tocar em Contratos ou Ocorrências.
 */
export function isStepSatisfied(
	step: TourStep,
	route: RouteLocationNormalizedLoaded,
): boolean {
	const expectation = step.expect;

	// Sem expectativa declarada não há o que conferir: confiar na pessoa é melhor
	// do que inventar um critério e barrar quem fez tudo certo.
	if (!expectation) return true;

	if (expectation.route && route.name !== expectation.route) return false;
	if (expectation.target && !isVisible(expectation.target)) return false;
	if (expectation.inputFilled && !isInputFilled(step.target)) return false;

	if (
		expectation.searchReflectedIn &&
		!isSearchReflected(expectation.searchReflectedIn.input, expectation.searchReflectedIn.results)
	) {
		return false;
	}

	return true;
}

export const DEFAULT_BLOCKED_MESSAGE =
	'Ainda não vi isso acontecer na tela. Faça o que está destacado — eu te espero aqui.';
